/**
 * reportRoleColors — semantic ROLE → concrete-color resolver for the
 * editorial SiteRAG report renderers.
 *
 * Why this exists: the report tree historically reached for literal
 * Tailwind classes (`text-slate-500`, `bg-blue-50`, `text-emerald-600`,
 * `border-slate-200`) for chrome — sub-section labels, recommendation
 * rules, open-question tags, callout backgrounds, verified citations,
 * figure captions. Those literals ignore the tenant's `ReportTheme`
 * entirely, so a journal/institutional/monograph preset (warm paper +
 * teal accent, cool white + navy accent, …) still rendered cold slate
 * chrome and primary-blue callouts.
 *
 * `roleColor(theme, role)` maps a small, closed set of EDITORIAL roles
 * to concrete CSS color strings derived from the theme's own tokens
 * (headingColor / bodyTextColor / primaryColor / positive/warning/
 * negativeColor) via the EXISTING `tonedTheme()` ramp helper. Every
 * editorial renderer reads `roleColor(theme, ...)` instead of a literal
 * Tailwind class, so the chrome follows the active preset — and legacy
 * (no-preset) reports, which resolve to DEFAULT_THEME, keep a visually
 * consistent cool-slate/cyan-accent look.
 *
 * Mirrors the user-approved editorial reference
 * (docs/design/journal-section-layouts.html,
 *  docs/design/journal-report-full.html): the CSS custom properties
 * there — --ink/--body/--muted/--faint/--hair/--accent/--accent-soft/
 * --go/--cond/--nogo — map one-to-one onto the roles below.
 *
 * PURE / synchronous / null-safe: every role resolves to a valid color
 * string for any partial/empty theme by falling through to
 * DEFAULT_THEME-derived tones. Safe to inline into render bodies and
 * `style={{ ... }}` props without memoization (≈ a few dozen calls per
 * report render).
 */
import { DEFAULT_THEME, type ReportTheme } from '../types/reportTheme';
import { tonedTheme } from './themeColor';
import { tone } from './tone';
import { wcagContrastRatio } from './resolveHeroCoverBackground';

/**
 * The report CONTENT is a printed DOCUMENT: a white page + dark ink, ALWAYS,
 * regardless of the app's light/dark chrome. The on-screen report tree is
 * force-locked white by the `[data-theme-scope='light']` CSS island
 * (src/index.css), and the PDF/print path mounts the SAME viewer, so the page
 * background under the report content is `#ffffff` everywhere.
 *
 * Inline `style={{ color: roleColor(...) }}` BYPASSES that CSS-variable island
 * (a literal hex is not a CSS var), so the neutral text roles MUST be readable
 * on white at the source — they cannot rely on the island re-asserting tokens.
 * Hence the neutral ramp below is derived against this constant document base
 * (NOT the preset's `backgroundColor`, which may be a warm/pale paper that
 * silently lightened muted/faint into the sub-WCAG grays that washed out).
 */
const DOC_BG = '#ffffff';

/**
 * The near-black ink the report island uses for `--text-primary`
 * (src/index.css). Neutral roles that fail the document-readability floor are
 * darkened TOWARD this so a light-ink / pale preset can't render invisible body
 * text on the white page.
 */
const DOC_INK = '#0e1419';

// ── Reader color MODE (light / dark reading surface) ──────────────────────
//
// The report is force-locked light for EXPORT (PDF/print/DOCX) and for every
// attestation baseline. The ON-SCREEN reader may additionally follow the app's
// light/dark toggle (see `useReportReaderMode.tsx`). Rather than thread a
// `mode` argument through all ~850 `roleColor()` call sites, the resolved mode
// RIDES on the theme object under a Symbol key: `attachReaderMode(theme,'dark')`
// stamps it, `roleColor(theme, role)` reads it. A Symbol key is invisible to
// `JSON.stringify`, so a stamped theme can NEVER leak the transient mode into
// Firestore/localStorage persistence. Default (unstamped) ⇒ 'light', so every
// existing caller + baseline is byte-identical.
export type ReportReaderMode = 'light' | 'dark';
const READER_MODE_KEY: unique symbol = Symbol('reportReaderMode');

/** Return a shallow copy of `theme` carrying the reader mode under a Symbol key
 *  (render-only; never serialized). */
export function attachReaderMode<T extends object>(theme: T, mode: ReportReaderMode): T {
  return { ...(theme as object), [READER_MODE_KEY]: mode } as T;
}

/** Read the reader mode a theme carries (default 'light' when unstamped). */
export function getThemeReaderMode(theme: unknown): ReportReaderMode {
  return theme != null
    && typeof theme === 'object'
    && (theme as Record<symbol, unknown>)[READER_MODE_KEY] === 'dark'
    ? 'dark'
    : 'light';
}

/** The on-screen report page background in DARK reader mode. A single deep
 *  graphite constant shared by the inline frame background AND the CSS island
 *  (`index.css` — keep the two in sync) so the sheet + tokens agree. Content
 *  colours still derive per-theme via the on-dark ramp below. */
export const READER_DARK_PAGE_BG = '#0b0f14';

/** Readability floors on the white document page (WCAG-derived):
 *  - ink / body are normal text  → AA-strong 7:1 floor (kept punchy);
 *  - muted is secondary text     → AA 4.5:1 floor (label/caption legibility);
 *  - faint is meta/large text    → the 3:1 large-text/UI floor.
 *  The retuned mix weights below already clear these for every shipped preset;
 *  the clamp is the SAFETY NET that rescues any future (or pathological)
 *  light-ink preset so report content can never wash out on white. */
const INK_BODY_MIN_CONTRAST = 7;

/** WCAG contrast of `hex` against the white document page (≥1; higher = darker
 *  on white). Falls back to 1 (worst case) for an unparseable color so the
 *  clamp below treats it as "needs darkening" rather than throwing. */
function contrastOnWhite(hex: string): number {
  return wcagContrastRatio(hex, DOC_BG) ?? 1;
}

/**
 * Darken `hex` toward the document ink until it clears `minContrast` against
 * the white page. Used to clamp the ink/body BASE before the muted/faint tones
 * are derived from it, so neutral report text is always dark-on-white
 * regardless of the active preset. Brand roles (accent/positive/warning/
 * negative) are intentionally NOT passed through this — their per-preset hue is
 * preserved. Pure; bounded loop (monotonic toward DOC_INK, ≤ ~30 steps).
 */
function ensureDarkOnWhite(hex: string, minContrast: number): string {
  if (contrastOnWhite(hex) >= minContrast) return hex;
  let out = hex;
  // Progressively pull toward the near-black ink. Each step mixes more ink in;
  // the final step is a hard floor at DOC_INK (always >> any text contrast).
  for (let step = 1; step <= 30; step += 1) {
    out = mixHex(DOC_INK, hex, Math.min(1, step * 0.06), DOC_INK);
    if (contrastOnWhite(out) >= minContrast) return out;
  }
  return DOC_INK;
}

/**
 * The closed set of editorial roles. Mapping to the reference CSS vars:
 *
 *   ink          → --ink           heading/strong ink (headingColor)
 *   body         → --body          paragraph/body text (bodyTextColor)
 *   muted        → --muted         secondary labels, captions
 *   faint        → --faint         faintest meta text
 *   hair         → --hair          hairline rules (subsection top, table rows)
 *   rule         → --hair-strong   stronger structural rules (section underline)
 *   subtleBg     → --paper-2       faint neutral wash (zebra striping)
 *   accent       → --accent        brand accent ink (labels, left-rules)
 *   accentSoft   → --accent-soft   faint accent wash (callout background)
 *   positive     → --go            verified / positive / go ink
 *   positiveSoft → (positive 50)   faint positive wash
 *   warning      → --cond          conditional / warning ink (oq tag)
 *   warningSoft  → (warning 50)    faint warning wash
 *   negative     → --nogo          no-go / negative ink
 *   negativeSoft → (negative 50)   faint negative wash
 */
export type ReportColorRole =
  | 'ink'
  | 'body'
  | 'muted'
  | 'faint'
  | 'hair'
  | 'rule'
  | 'subtleBg'
  | 'accent'
  | 'accentSoft'
  | 'positive'
  | 'positiveSoft'
  | 'warning'
  | 'warningSoft'
  | 'negative'
  | 'negativeSoft';

/** Trim and reject empty strings; undefined/blank ⇒ undefined. */
function nonEmpty(s: string | undefined | null): string | undefined {
  return s && s.trim() ? s.trim() : undefined;
}

/**
 * Mix two hex colors in sRGB by `weight` of `a` (0..1). Used to derive
 * the neutral muted/faint/hair tones from the theme's heading ink +
 * background, so a warm-paper preset gets warm-grey hairlines and a
 * cool-white preset gets cool-grey hairlines — rather than a fixed
 * slate. Returns `fallback` when either input is unparseable.
 */
function mixHex(a: string, b: string, weight: number, fallback: string): string {
  const parse = (hex: string): [number, number, number] | null => {
    const m = hex.trim().replace(/^#/, '');
    const e = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
    if (!/^[0-9a-f]{6}$/i.test(e)) return null;
    return [
      parseInt(e.slice(0, 2), 16),
      parseInt(e.slice(2, 4), 16),
      parseInt(e.slice(4, 6), 16),
    ];
  };
  const pa = parse(a);
  const pb = parse(b);
  if (!pa || !pb) return fallback;
  const w = Math.max(0, Math.min(1, weight));
  const to2 = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  const ch = (i: 0 | 1 | 2) => to2(pa[i] * w + pb[i] * (1 - w));
  return `#${ch(0)}${ch(1)}${ch(2)}`;
}

/**
 * Resolve an editorial ROLE to a concrete CSS color string for the given
 * theme. Theme tokens win; any missing/blank token falls through to the
 * corresponding DEFAULT_THEME token, so the function is total (always
 * returns a valid color) for any `Partial<ReportTheme>`-shaped input.
 *
 * `theme` is intentionally typed loosely (`Partial<ReportTheme>`) so
 * callers can pass an over-merged or stale object without a cast; the
 * fields it actually reads are back-filled from DEFAULT_THEME.
 */
export function roleColor(
  theme: Partial<ReportTheme> | null | undefined,
  role: ReportColorRole,
  /** Override the reader mode. Omitted ⇒ read the mode the theme carries
   *  (`getThemeReaderMode`), which is 'light' for every unstamped theme — so
   *  existing callers are byte-identical. Passed explicitly by tests. */
  mode?: ReportReaderMode,
): string {
  const t = theme ?? {};

  // Resolve every brand token with DEFAULT_THEME fall-through so blank /
  // missing tokens degrade gracefully. These RAW tokens back the ink
  // roles (accent/positive/warning/negative read the brand color
  // directly so risk/verdict ink matches the chart series + RAG dots).
  const inkRaw = nonEmpty(t.headingColor) ?? DEFAULT_THEME.headingColor;
  const bodyRaw = nonEmpty(t.bodyTextColor) ?? DEFAULT_THEME.bodyTextColor;
  const primary = nonEmpty(t.primaryColor) ?? DEFAULT_THEME.primaryColor;
  const positive = nonEmpty(t.positiveColor) ?? DEFAULT_THEME.positiveColor;
  const warning = nonEmpty(t.warningColor) ?? DEFAULT_THEME.warningColor;
  const negative = nonEmpty(t.negativeColor) ?? DEFAULT_THEME.negativeColor;

  // ── DARK reader mode ─────────────────────────────────────────────────────
  // On-screen only (export always resolves 'light'). Neutral + brand INK roles
  // reuse the existing theme-tinted on-dark ramp (`onInstrument` → derives
  // against `instrumentSurface`, lightens hues to clear the on-dark WCAG floor).
  // The SOFT roles are WASH BACKGROUNDS, not text — on dark they must be a faint
  // tint of the hue OVER the dark surface, not a light colour; a low-alpha wash
  // composites correctly over any dark backdrop. Mirrors how the light SOFT
  // roles are a pale tint of the hue on white.
  const m = mode ?? getThemeReaderMode(theme);
  if (m === 'dark') {
    switch (role) {
      case 'accentSoft':
        return withAlpha(primary, 0.16);
      case 'positiveSoft':
        return withAlpha(positive, 0.16);
      case 'warningSoft':
        return withAlpha(warning, 0.16);
      case 'negativeSoft':
        return withAlpha(negative, 0.16);
      default:
        return onInstrument(t, role);
    }
  }

  // Ramps drive the SOFT washes only (accentSoft/positiveSoft/…) so they
  // match the rest of the report (KPIs, alerts, chart tints). tonedTheme
  // is itself null-safe (each ramp falls back to a sensible default hex
  // when the token is blank). The 50-stop is the faint wash.
  const ramp = tonedTheme({
    primaryColor: primary,
    positiveColor: positive,
    warningColor: warning,
    negativeColor: negative,
  });

  // ── Document-readability clamp for the NEUTRAL roles ─────────────────
  // The report content is ALWAYS a white page + dark ink (CSS island + same
  // PDF mount). Clamp the ink/body base dark enough to clear the strong-text
  // floor on white, then derive muted/faint from the CLAMPED body mixed toward
  // the white DOCUMENT base (NOT the preset bg). Old code mixed body→presetBg
  // at 0.62/0.42, producing #8d96a2 (2.99:1) / #b2b8c0 (2.00:1) on white — the
  // washed-out sub-labels/captions/meta the report showed. Brand roles below
  // are left untouched, so per-preset accent/status hues survive.
  const ink = ensureDarkOnWhite(inkRaw, INK_BODY_MIN_CONTRAST);
  const body = ensureDarkOnWhite(bodyRaw, INK_BODY_MIN_CONTRAST);

  switch (role) {
    // ── Neutral text ramp (ink → body → muted → faint) ──────────────
    case 'ink':
      return ink;
    case 'body':
      return body;
    // muted = clamped body lightened toward the WHITE document page (≈
    // secondary labels). 0.88 keeps it ≥ WCAG AA 4.5:1 on white for every
    // shipped preset (DEFAULT 5.57:1 … proceedings 9.19:1).
    case 'muted':
      return mixHex(body, DOC_BG, 0.88, '#5d697b');
    // faint = clamped body lightened further toward white (meta text). 0.74
    // keeps it ≥ the 3:1 large-text/UI floor on white (DEFAULT 3.94:1 …
    // proceedings 5.84:1) — never the old 2.0:1 near-invisible gray.
    case 'faint':
      return mixHex(body, DOC_BG, 0.74, '#778190');

    // ── Structural rules / neutral washes ────────────────────────────
    // These are hairline RULES / zebra-stripe BACKGROUNDS (not text), so they
    // must stay PALE on the page. Mix the (clamped, dark) ink toward the white
    // document base so they read as faint neutral lines/washes on white in
    // every preset — the preset bg no longer leaks a too-light/too-dark tone.
    // hair = the faint hairline between subsections / table rows.
    case 'hair':
      return mixHex(ink, DOC_BG, 0.12, '#e2e3e5');
    // rule = stronger structural rule (section underline, table header).
    case 'rule':
      return mixHex(ink, DOC_BG, 0.24, '#c5c7cc');
    // subtleBg = the faintest neutral wash for zebra striping.
    case 'subtleBg':
      return mixHex(ink, DOC_BG, 0.05, '#f3f3f4');

    // ── Accent ───────────────────────────────────────────────────────
    case 'accent':
      // The brand accent ink itself (labels, left-rules) — the raw
      // primaryColor so it matches chart series / sidebar exactly.
      return primary;
    case 'accentSoft':
      return ramp.primary[50];

    // ── Status (positive / warning / negative) ───────────────────────
    case 'positive':
      return positive;
    case 'positiveSoft':
      return ramp.positive[50];
    case 'warning':
      return warning;
    case 'warningSoft':
      return ramp.warning[50];
    case 'negative':
      return negative;
    case 'negativeSoft':
      return ramp.negative[50];

    default: {
      // Exhaustiveness guard — if a new role is added to the union but
      // not handled above, TS flags it here at compile time. At runtime
      // (stale persisted value) fall through to body ink so nothing is
      // ever invisible.
      const _exhaustive: never = role;
      void _exhaustive;
      return body;
    }
  }
}

/**
 * Convenience: a translucent rgba derived from a role color, for faint
 * borders that should pick up the accent/status hue at low alpha
 * (mirrors the reference's `color-mix(in srgb, var(--cond) 40%, …)`
 * tag borders). Falls back to the opaque role color when the role
 * resolves to an unparseable string (never happens for the built-ins,
 * defensive only).
 */
export function roleColorAlpha(
  theme: Partial<ReportTheme> | null | undefined,
  role: ReportColorRole,
  alpha: number,
): string {
  return withAlpha(roleColor(theme, role), alpha);
}

/**
 * Build a translucent rgba() from an already-resolved hex color. Shared by
 * `roleColorAlpha` (role → alpha) and the dark instrument-panel chips (which
 * need a wash of the ON-DARK resolved ink, not the light-surface role). Returns
 * the input unchanged when it is not a parseable 3/6-digit hex (defensive).
 */
export function withAlpha(hex: string, alpha: number): string {
  const m = hex.trim().replace(/^#/, '');
  const e = m.length === 3 ? m.split('').map((c) => c + c).join('') : m;
  if (!/^[0-9a-f]{6}$/i.test(e)) return hex;
  const r = parseInt(e.slice(0, 2), 16);
  const g = parseInt(e.slice(2, 4), 16);
  const b = parseInt(e.slice(4, 6), 16);
  const a = Math.max(0, Math.min(1, alpha));
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

// Re-export a softer, hairline border derived from the brand accent —
// used directly by EditorialBadge for the "Verified"/tag outline so the
// outline picks up the status hue at low alpha. Kept here (not inline)
// so the convert agents share one definition.
export function roleBorder(
  theme: Partial<ReportTheme> | null | undefined,
  role: ReportColorRole,
): string {
  return roleColorAlpha(theme, role, 0.42);
}

// ── Dark "instrument-panel" surface (K3 — premium metric panels) ──────────
//
// The redesign's signature gravity move is a DARK metric card (the ELCC /
// "FIRM-DELIVERABLE" panel, the IX "204.9 km" card): a deep surface, mono
// small-caps labels, a serif hero value, dotted-leader sub-rows. The report
// page is always white, so a dark island here is a deliberate inversion — and
// it must work in ANY preset WITHOUT a hardcoded color. We derive it from the
// theme's OWN tokens so the panel carries the preset's hue: the surface is the
// (heading) ink mixed hard toward near-black; text ON it is lightened toward
// white. NO raw hex leaves these helpers — primitives call them and never bake
// a color, so the report-island color sentinel stays clean.

/** Near-black floor the dark surface is pulled toward (the inverse of DOC_INK,
 *  i.e. a deep neutral the heading hue tints). Kept private. */
const PANEL_FLOOR = '#11161c';
/** The light base on-panel neutral text is mixed toward (off-white, not pure
 *  white, so it reads as paper-on-ink rather than glare). */
const PANEL_LIGHT = '#f4f5f7';

/** Readability floor for text ON the dark instrument surface (WCAG large/UI
 *  3:1 for labels, comfortably cleared by the mixes below for values). */
const ON_PANEL_MIN_CONTRAST = 4.5;

/** WCAG contrast of `hex` against `bg` (≥1). Falls back to 1 (treat as
 *  needs-lightening) for an unparseable pair. */
function contrastOn(hex: string, bg: string): number {
  return wcagContrastRatio(hex, bg) ?? 1;
}

/** Lighten `hex` toward the off-white panel base until it clears `minContrast`
 *  against `surface`. Mirror of `ensureDarkOnWhite` for the inverted island, so
 *  a dark-ink brand color (navy accent, deep-green positive) can't render
 *  invisible on the dark panel. Pure; bounded monotonic loop. */
function ensureLightOnDark(hex: string, surface: string, minContrast: number): string {
  if (contrastOn(hex, surface) >= minContrast) return hex;
  let out = hex;
  for (let step = 1; step <= 30; step += 1) {
    out = mixHex(PANEL_LIGHT, hex, Math.min(1, step * 0.06), PANEL_LIGHT);
    if (contrastOn(out, surface) >= minContrast) return out;
  }
  return PANEL_LIGHT;
}

/**
 * The DARK instrument-panel surface for the active theme. The heading ink
 * (clamped dark-on-white above is the raw token here, pre-clamp) is mixed hard
 * toward the near-black floor so the panel carries a hint of the preset's
 * heading hue while staying a deep, print-safe dark. Pure / total.
 */
export function instrumentSurface(theme: Partial<ReportTheme> | null | undefined): string {
  const t = theme ?? {};
  const inkRaw = nonEmpty(t.headingColor) ?? DEFAULT_THEME.headingColor;
  // 0.22 of the heading ink over the near-black floor → a deep surface that
  // still tints toward the preset (navy presets read cooler, warm presets
  // warmer) without ever lightening into a mid-grey.
  return mixHex(inkRaw, PANEL_FLOOR, 0.22, PANEL_FLOOR);
}

/**
 * Resolve an editorial ROLE to a concrete color readable ON the dark
 * instrument surface for `theme`. Neutral roles map onto an off-white ramp
 * (value → label → faint sub-text); brand/status roles keep their hue but are
 * lightened to clear the on-dark readability floor. Used by
 * `EditorialInstrumentPanel` / the dark `EditorialHeroMetric` tone so the panel
 * never bakes a literal. Pure / total.
 */
export function onInstrument(
  theme: Partial<ReportTheme> | null | undefined,
  role: ReportColorRole,
): string {
  const surface = instrumentSurface(theme);
  switch (role) {
    // Neutral on-dark ramp (hero value → label → faint meta).
    case 'ink':
    case 'body':
      return PANEL_LIGHT;
    case 'muted':
      // mono small-caps labels — a calmer off-white.
      return mixHex(PANEL_LIGHT, surface, 0.74, '#c2c6cc');
    case 'faint':
      return mixHex(PANEL_LIGHT, surface, 0.55, '#9aa0a8');
    // Hairline rules / washes ON the dark surface — lift the light base a
    // little off the surface so dividers read without glare.
    case 'hair':
      return mixHex(PANEL_LIGHT, surface, 0.18, '#3a4048');
    case 'rule':
      return mixHex(PANEL_LIGHT, surface, 0.3, '#4a515a');
    case 'subtleBg':
      return mixHex(PANEL_LIGHT, surface, 0.08, '#222933');
    // Brand / status roles keep their hue, lightened to read on dark.
    // Force the inner resolve to LIGHT: `onInstrument` is itself the dark ramp,
    // and the dark branch of `roleColor` delegates here — reading the theme's
    // (dark) mode again would recurse. Light is also correct: we want the raw
    // per-preset hue, then lighten it for the dark surface.
    default:
      return ensureLightOnDark(roleColor(theme, role, 'light'), surface, ON_PANEL_MIN_CONTRAST);
  }
}

// Internal helper exposed for unit tests only — derives a stop off the
// accent ramp so a test can assert soft tints come from tone(), not a
// literal. Not part of the public renderer API.
export function __toneStopForTest(hex: string, stop: 50 | 700): string {
  return tone(hex, stop);
}
